import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import config from './config';
import TeamSlotGrid from './components/TeamSlotGrid';
import leagueMembershipApi from './services/leagueMembershipApi';

const JoinLeague = ({ onBack, showToast, currentUser: propCurrentUser }) => {
  const navigate = useNavigate();
  const [inviteDetails, setInviteDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [teamSlots, setTeamSlots] = useState([]);

  useEffect(() => {
    // Get invite token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (!token) {
      setError('No invitation token found');
      setLoading(false);
      return;
    }

    fetchInviteDetails(token);
  }, []);

  // Update local currentUser state when prop changes
  useEffect(() => {
    if (propCurrentUser) {
      setCurrentUser(propCurrentUser);
    }
  }, [propCurrentUser]);

  const fetchInviteDetails = async (token) => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/invites/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid invitation');
      }
      
      const data = await response.json();
      setInviteDetails(data);

      try {
        const slotsData = await leagueMembershipApi.getTeamSlots(data.league_id);
        setTeamSlots(slotsData.slots || []);
      } catch (slotsErr) {
        console.error('Error fetching team slots:', slotsErr);
        setTeamSlots([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching invite details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!currentUser) {
      showToast('Please log in to accept this invitation', 'error');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    try {
      setAccepting(true);
      const response = await fetch(`${config.API_URL}/invites/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: currentUser.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to accept invitation');
      }

      const result = await response.json();
      showToast(`Successfully joined ${result.league_name}!`, 'success');
      
      // Clear any stored invite tokens
      localStorage.removeItem('pendingInviteToken');
      
      // Redirect to league home after a short delay
      setTimeout(() => {
        navigate(`/league/${result.league_id}`);
      }, 2000);
      
    } catch (err) {
      showToast(err.message, 'error');
      console.error('Error accepting invite:', err);
    } finally {
      setAccepting(false);
    }
  };

  const handleLogin = () => {
    // Store the current URL to redirect back after login
    localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    navigate('/login');
  };

  const handleRetryInvite = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (!token) {
      setError('No invitation token found');
      return;
    }
    setError(null);
    setLoading(true);
    fetchInviteDetails(token);
  };

  const handleSignUp = () => {
    // Store the invite token in localStorage for the signup process
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      localStorage.setItem('pendingInviteToken', token);
    }
    
    // Store the current URL to redirect back after signup
    localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <LoadingSpinner text="Loading invitation..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <ErrorMessage 
          error={error} 
          onRetry={handleRetryInvite}
          title="Invalid Invitation"
        />
        <button
          onClick={onBack}
          style={{
            background: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            marginTop: 20
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  if (!inviteDetails) {
    return null;
  }

  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title">🏈 Fantasy Football Classic™ 🏈</h1>
      </header>
      
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 40 }}>
        <div style={{ 
          background: 'white', 
          borderRadius: 12, 
          padding: 32, 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: 24
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h2 style={{ color: '#2563eb', margin: '0 0 8px 0' }}>
              League Invitation
            </h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              You've been invited to join a fantasy football league!
            </p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ 
              background: '#f8fafc', 
              padding: 20, 
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>
                {inviteDetails.league_name}
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Commissioner</div>
                  <div style={{ color: '#6b7280' }}>{inviteDetails.inviter_username}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>League Size</div>
                  <div style={{ color: '#6b7280' }}>
                    {inviteDetails.teams_joined_label ||
                      `${inviteDetails.teams_joined_count ?? inviteDetails.member_count} teams joined`}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Scoring</div>
                  <div style={{ color: '#6b7280' }}>{inviteDetails.scoring_type}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Draft Type</div>
                  <div style={{ color: '#6b7280' }}>{inviteDetails.draft_type}</div>
                </div>
              </div>

              <div style={{ 
                background: (inviteDetails.is_full || inviteDetails.membership_locked) ? '#fef2f2' : '#f0fdf4', 
                border: `1px solid ${(inviteDetails.is_full || inviteDetails.membership_locked) ? '#fecaca' : '#bbf7d0'}`,
                borderRadius: 6,
                padding: 12,
                textAlign: 'center'
              }}>
                <div style={{ 
                  color: (inviteDetails.is_full || inviteDetails.membership_locked) ? '#dc2626' : '#059669',
                  fontWeight: 600
                }}>
                  {inviteDetails.membership_locked || inviteDetails.draft_has_begun
                    ? 'Draft started — invites closed'
                    : inviteDetails.is_full
                      ? 'League is Full'
                      : 'Spots Available'}
                </div>
              </div>

              <TeamSlotGrid slots={teamSlots} title="Team slot availability" />
            </div>
          </div>

          {!currentUser ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#6b7280', marginBottom: 20 }}>
                You need to create an account or log in to accept this invitation.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSignUp}
                  style={{
                    background: '#059669',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Sign Up
                </button>
                <button
                  onClick={handleLogin}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Log In
                </button>
                <button
                  onClick={onBack}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Back to Home
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              {inviteDetails.membership_locked || inviteDetails.draft_has_begun ? (
                <div>
                  <p style={{ color: '#dc2626', marginBottom: 20, lineHeight: 1.5 }}>
                    This league&apos;s draft has already started. Email invitations can no longer be accepted.
                    Ask your commissioner to assign you using <strong>Transfer team</strong> in league settings
                    if you are replacing a manager.
                  </p>
                  <button
                    onClick={onBack}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: 6,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Back to Home
                  </button>
                </div>
              ) : inviteDetails.is_full ? (
                <div>
                  <p style={{ color: '#dc2626', marginBottom: 20 }}>
                    This league is currently full. Please contact the commissioner for more information.
                  </p>
                  <button
                    onClick={onBack}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: 6,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Back to Home
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ color: '#6b7280', marginBottom: 20 }}>
                    Welcome, <strong>{currentUser.username}</strong>! Ready to join the league?
                  </p>
                  <button
                    onClick={handleAcceptInvite}
                    disabled={accepting}
                    style={{
                      background: accepting ? '#9ca3af' : '#059669',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: 6,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: accepting ? 'not-allowed' : 'pointer',
                      marginRight: 12
                    }}
                  >
                    {accepting ? 'Joining...' : 'Accept Invitation'}
                  </button>
                  <button
                    onClick={onBack}
                    disabled={accepting}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: 6,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: accepting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
          <p>
            This invitation expires on {new Date(inviteDetails.expires_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default JoinLeague; 